<template>
  <div class="mt-4">
    <div class="row">
      <div class="col">
        <h3>Object Model Diagram</h3>
        <p>Can drag nodes and zoom (unset position with shift). Click a node to jump to its builder. Toggle displaying node types with the legend. Press Organize a few times if everything is jumbled.</p>
        <button class="btn green" v-on:click="savePng">Save PNG</button>
        <button class="btn green" v-on:click="reset">Reset</button>
        <button class="btn green" v-on:click="generateGraph">Organize</button>
        <div id="graph" class="d3-graph"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import * as d3 from "d3";
import { ModelType } from "~/enums/modelType";
import d3ToPng from "~/helpers/d3-svg-to-png";
import selectedMixin from "../mixins/selectedMixin";

export default Vue.extend({
  data() {
    return {
      graph: { nodes: [], links: [] },
      filteredGraph: { nodes: [], links: [] },
      linkedByIndex: {},
      legend: {
        "Object Models": true,
        "List Views": false,
        "Detail Views": false,
        Mappings: true,
        Forms: true,
        "Process Flows": true,
        SObjects: true,
      },
      linkStrength: 0.025,
      chargeStrength: -40,
      simulationOn: true,
    };
  },
  mixins: [selectedMixin],
  mounted() {
    this.reset();
  },
  methods: {
    isConnected(a, b) {
      return this.linkedByIndex[`${a.id},${b.id}`] || this.linkedByIndex[`${b.id},${a.id}`] || a.id === b.id;
    },
    async reset() {
      await this.fetchDepGraph();
      this.setFilteredGraph();
      this.generateGraph();
    },
    async fetchDepGraph() {
      try {
        const res = await fetch(`/experimental/object_models/${this.selectedObjectModel.id}/dependency_graph`, {
          method: "GET",
          headers: {
            "X-CSRF-Token": $('meta[name="csrf-token"]').attr("content"),
          },
        });
        const json = await res.json();
        this.graph = json.graph;
      } catch (e) {
        this.errorMessage = "Sorry, something went wrong. ";
      }
    },
    setFilteredGraph() {
      const filteredNodes = this.graph.nodes.filter((node) => {
        switch (node.type) {
          case ModelType.ObjectModel:
            return this.legend["Object Models"];
          case ModelType.ContentListView:
            return this.legend["List Views"];
          case ModelType.ContentDetailView:
            return this.legend["Detail Views"];
          case ModelType.Form:
            return this.legend["Forms"];
          case ModelType.Mapping:
            return this.legend["Mappings"];
          case ModelType.ProcessFlow:
            return this.legend["Process Flows"];
          case ModelType.SObject:
            return this.legend["SObjects"];

          default:
            return true;
        }
      });

      const nodeIds = filteredNodes.map((n) => n.id);

      const filteredLinks = this.graph.links.filter((link) => {
        return (
          (nodeIds.includes(link.source) || nodeIds.includes(link.source.id)) &&
          (nodeIds.includes(link.target) || nodeIds.includes(link.target.id))
        );
      });

      this.filteredGraph = { nodes: filteredNodes, links: filteredLinks };

      this.filteredGraph.links.forEach(d => {
        this.linkedByIndex[`${d.source},${d.target}`] = 1;
      });
    },
    savePng() {
      d3ToPng("#graph>svg", `${this.selectedObjectModel.title} Diagram`, {
        scale: 5,
        download: true,
      });
    },
    generateGraph() {
      // reset
      document.querySelector("#graph>svg")?.remove();

      // canvas set up
      const h = 600;
      const w = 1040;
      const svg = d3.select("#graph").append("svg").attr("width", w).attr("height", h).attr("viewBox", [0, 0, w, h]);

      const g = svg.append("g");

      // simulation setup
      const simulation = d3
        .forceSimulation()
        .force(
          "link",
          d3
            .forceLink()
            .id((d) => d.id)
            .strength((link) => this.linkStrength)
        )
        .force("charge", d3.forceManyBody().strength(this.chargeStrength))
        .force("center", d3.forceCenter(w / 2, h / 2));

      // drag and drop setup
      const dragDrop = d3
        .drag()
        .on("start", (node) => {
          node.fx = node.x;
          node.fy = node.y;
        })
        .on("drag", (node) => {
          simulation.alphaTarget(0.7).restart();
          node.fx = d3.event.x;
          node.fy = d3.event.y;
        })
        .on("end", (node) => {
          if (!d3.event.active) {
            simulation.alphaTarget(0);
          }
        });

      // helper for node coloring
      const getNodeColor = (node) => {
        switch (node.type) {
          case ModelType.ObjectModel:
            return "#20723b";
          case ModelType.ContentListView:
            return "#9e6710";
          case ModelType.ContentDetailView:
            return "#257d93";
          case ModelType.Form:
            return "#3876cc";
          case ModelType.Mapping:
            return "#BF4A67";
          case ModelType.SObject:
            return "#6F58C9";
          case ModelType.ProcessFlow:
            return "#ea8685";

          default:
            return "white";
        }
      };

      // create svg elements
      const linkElements = g
        .selectAll("line")
        .data(this.filteredGraph.links)
        .enter()
        .append("line")
        .attr("stroke-width", 1)
        .attr("stroke", "#E6E6E6")
        .on('mouseout.fade', fade(1))

      const nodeElements = g
        .selectAll("circle")
        .data(this.filteredGraph.nodes)
        .enter()
        .append("circle")
        .attr("r", 10)
        .attr("fill", getNodeColor)
        .on("click", clicked)
        .on('mouseover.fade', fade(0.1))
        .on('mouseout.fade', fade(1))

      const textElements = g
        .selectAll("text")
        .data(this.filteredGraph.nodes)
        .enter()
        .append("text")
        .text((node) => node.title)
        .attr("font-size", 15)
        .attr("dx", 15)
        .attr("dy", 4);


      // enable drag and drop
      nodeElements.call(dragDrop);

      // simulation tick
      simulation.nodes(this.filteredGraph.nodes).on("tick", () => {
        nodeElements.attr("cx", (node) => node.x).attr("cy", (node) => node.y);
        textElements.attr("x", (node) => node.x).attr("y", (node) => node.y);
        linkElements
          .attr("x1", (link) => link.source.x)
          .attr("y1", (link) => link.source.y)
          .attr("x2", (link) => link.target.x)
          .attr("y2", (link) => link.target.y);
      });

      simulation.force("link").links(this.filteredGraph.links);

      // setup zoom
      svg.call(
        d3
          .zoom()
          .extent([
            [0, 0],
            [w, h],
          ])
          .scaleExtent([0.2, 8])
          .on("zoom", zoomed)
      );

      function zoomed() {
        g.attr("transform", d3.event.transform);
      }

      const that = this;
      function fade(opacity) {
        return d => {
          nodeElements.style('stroke-opacity', function (o) {
            const thisOpacity = that.isConnected(d, o) ? 1 : opacity;
            this.setAttribute('fill-opacity', thisOpacity);
            return thisOpacity;
          });

          linkElements.style('stroke-opacity', o => (o.source === d || o.target === d ? 1 : opacity));

          textElements.style('opacity', function (o) {
            const thisOpacity = that.isConnected(d, o) ? 1 : opacity;
            this.setAttribute('fill-opacity', thisOpacity);
            return thisOpacity;
          });
        };
      }

      function clicked(d, i) {
        if (d3.event.shiftKey) {
          d.fx = null;
          d.fy = null;
          return;
        }

        if (d.link) {
          window.open(d.link);
        }
      }

      this.generateLegend();
    },
    generateLegend() {
      const clicked = (d, i) => {
        this.legend[d] = !this.legend[d];
        this.setFilteredGraph();
        this.generateGraph();
      };

      const svg = d3.select("#graph>svg");

      const keys = Object.keys(this.legend);
      const colors = {
        "Object Models": "#20723b",
        "List Views": "#9e6710",
        "Detail Views": "#257d93",
        Mappings: "#BF4A67",
        Forms: "#3876cc",
        "Process Flows": "#ea8685",
        SObjects: "#6F58C9",
      };

      svg
        .selectAll("legendDots")
        .data(keys)
        .enter()
        .append("circle")
        .attr("cx", 30)
        .attr("cy", function (d, i) {
          return 30 + i * 25;
        })
        .attr("r", 7)
        .style("fill", (d) => {
          return this.legend[d] ? colors[d] : "#DDD";
        })
        .on("click", clicked);

      svg
        .selectAll("mylabels")
        .data(keys)
        .enter()
        .append("text")
        .attr("x", 50)
        .attr("y", function (d, i) {
          return 35 + i * 25;
        })
        .text(function (d) {
          return d;
        })
        .attr("text-anchor", "left")
        .style("alignment-baseline", "middle");
    },
  },
});
</script>